import { Database } from '@/database.types';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { useCedarStore } from '@/store/CedarStore';

// Remove the existing global client
let supabaseClient: SupabaseClient | null = null;

export interface SupabaseConfig {
	supabaseUrl?: string;
	supabaseKey?: string;
}

/**
 * Initialize the Supabase client with provided credentials or environment variables
 */
export const initializeSupabase = (
	config?: SupabaseConfig
): SupabaseClient<Database> => {
	// Always create a new client when config is provided
	if (config && (config.supabaseUrl || config.supabaseKey)) {
		supabaseClient = null;
	}

	if (supabaseClient) return supabaseClient;

	// Use provided credentials or fallback to environment variables
	const supabaseUrl =
		config?.supabaseUrl || process.env.NEXT_PUBLIC_CEDAR_SUPABASE_URL;
	const supabaseKey =
		config?.supabaseKey || process.env.NEXT_PUBLIC_CEDAR_SUPABASE_KEY;

	if (!supabaseUrl || !supabaseKey) {
		throw new Error(
			'Supabase URL and key must be provided either through config or environment variables'
		);
	}

	// Add default headers configuration
	supabaseClient = createClient<Database>(supabaseUrl, supabaseKey, {
		auth: {
			persistSession: false,
		},
		global: {
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
		},
	});

	return supabaseClient;
};

/**
 * Get the current Supabase client or initialize with provided config
 * Falls back to the configuration stored in CedarStore if available
 */
export const getSupabaseClient = (
	config?: SupabaseConfig
): SupabaseClient<Database> => {
	// If we already have a client, return it
	if (supabaseClient) return supabaseClient;

	// If explicit config is provided, use it directly
	if (config) {
		return initializeSupabase(config);
	}

	// Try to get config from the store as fallback
	try {
		const storeConfig = useCedarStore.getState().supabaseConfig;
		if (storeConfig) {
			return initializeSupabase(storeConfig);
		}
	} catch (e) {
		// If store is not available (e.g., during SSR or initialization),
		// silently continue and use environment variables
		console.debug('Could not access CedarStore for Supabase config:', e);
	}

	// Fall back to environment variables
	return initializeSupabase();
};
